import { replaceEqualDeep, useQuery } from '@tanstack/react-query'
import axios from 'axios'
import moment from 'moment'
import ExchangeService from 'src/services/ExchangeService'
import {
  normalizeExportOffers,
  normalizeFreeCarriersOffers,
  normalizeGeneralOffers,
  normalizeImportOffers,
  normalizePostImportOffers
} from 'src/features/stocks/ducks/normalizers'
import { getValhallaRouteDetails } from './valhalla'
import { scheduleMapQueryRefresh } from './scheduleMapQueryRefresh'
import {
  EXCHANGE_MAP_TYPES,
  getMapDistanceKm,
  getRoundTripCenterMapLocation,
  getRoundTripCandidateTypes,
  isPreplannedRoundTripOffer,
  isSameOfferLocation,
  normalizeMapLayers
} from './offerMapUtils'

export const MAP_LIST_PAGE_LIMIT = 100

const MAP_LIST_PAGE_REQUEST_CONCURRENCY = 4
const MAP_POINTS_STALE_TIME = 15 * 1000
const MAP_LIST_STALE_TIME = 15 * 1000
const ROUND_TRIP_STALE_TIME = 60 * 1000
const MAP_CACHE_TIME = 5 * 60 * 1000
const ROUTE_STALE_TIME = 60 * 60 * 1000
const ROUTE_CACHE_TIME = 60 * 60 * 1000
const ROUTE_MEMORY_CACHE_LIMIT = 1000
const routeMemoryCache = new Map()

const MAP_EXCHANGES = {
  [EXCHANGE_MAP_TYPES.EXPORT]: 'export-cargo',
  [EXCHANGE_MAP_TYPES.IMPORT]: 'import-cargo',
  [EXCHANGE_MAP_TYPES.GENERAL]: 'general-cargo',
  [EXCHANGE_MAP_TYPES.POST_IMPORT]: 'post-import-containers',
  [EXCHANGE_MAP_TYPES.FREE_CARRIERS]: 'free-carriers'
}

const MAP_NORMALIZERS = {
  [EXCHANGE_MAP_TYPES.EXPORT]: normalizeExportOffers,
  [EXCHANGE_MAP_TYPES.IMPORT]: normalizeImportOffers,
  [EXCHANGE_MAP_TYPES.GENERAL]: normalizeGeneralOffers,
  [EXCHANGE_MAP_TYPES.POST_IMPORT]: normalizePostImportOffers,
  [EXCHANGE_MAP_TYPES.FREE_CARRIERS]: normalizeFreeCarriersOffers
}

const ROUND_TRIP_FILTER_CONFIG = {
  [EXCHANGE_MAP_TYPES.EXPORT]: {
    originDateFrom: 'loading_date_from',
    originDateTo: 'loading_date_to'
  },
  [EXCHANGE_MAP_TYPES.GENERAL]: {
    originDateFrom: 'loading_date_from',
    originDateTo: 'loading_date_to'
  }
}

const withRawOffer = offer => ({ ...offer, raw: offer })

const getFormattedMapFilters = filters =>
  Object.keys(filters).reduce(
    (formattedFilters, key) => ({
      ...formattedFilters,
      [`filter[${key}]`]: filters[key]
    }),
    {}
  )

const getMapGroupKey = group =>
  group
    ? `${group.role}:${(group.locationIds || [])
        .map(String)
        .sort()
        .join(',')}`
    : null

const getRouteCacheKey = (from, to) =>
  `${from.join(':')}->${to.join(':')}:truck`

const getLocallyCachedRoute = (from, to) => {
  const cacheKey = getRouteCacheKey(from, to)
  const cachedRoute = routeMemoryCache.get(cacheKey)
  if (cachedRoute && cachedRoute.expiresAt > Date.now()) {
    routeMemoryCache.delete(cacheKey)
    routeMemoryCache.set(cacheKey, cachedRoute)
    return cachedRoute.promise
  }
  if (cachedRoute) routeMemoryCache.delete(cacheKey)

  const promise = getValhallaRouteDetails(from, to).catch(error => {
    routeMemoryCache.delete(cacheKey)
    throw error
  })
  routeMemoryCache.set(cacheKey, {
    expiresAt: Date.now() + ROUTE_CACHE_TIME,
    promise
  })
  if (routeMemoryCache.size > ROUTE_MEMORY_CACHE_LIMIT) {
    routeMemoryCache.delete(routeMemoryCache.keys().next().value)
  }

  return promise
}

const requestWithSignal = async (signal, request) => {
  if (!signal) return request()

  const source = axios.CancelToken.source()
  const cancelRequest = () => source.cancel('Query was cancelled')
  if (signal.aborted) {
    cancelRequest()
    source.token.throwIfRequested()
  }
  signal.addEventListener('abort', cancelRequest, { once: true })

  try {
    return await request({ cancelToken: source.token })
  } finally {
    signal.removeEventListener('abort', cancelRequest)
  }
}

export const exchangeOffersMapKeys = {
  all: ['exchange-offers-map'],
  tab: (exchangeType, activeTab) => [
    ...exchangeOffersMapKeys.all,
    exchangeType,
    'tab',
    String(activeTab || '')
  ],
  pointsScope: (exchangeType, activeTab) => [
    ...exchangeOffersMapKeys.tab(exchangeType, activeTab),
    'points'
  ],
  points: (exchangeType, activeTab, filters) => [
    ...exchangeOffersMapKeys.pointsScope(exchangeType, activeTab),
    filters
  ],
  listsScope: (exchangeType, activeTab) => [
    ...exchangeOffersMapKeys.tab(exchangeType, activeTab),
    'lists'
  ],
  list: (exchangeType, activeTab, group, filters, page) => [
    ...exchangeOffersMapKeys.listsScope(exchangeType, activeTab),
    getMapGroupKey(group),
    filters,
    page
  ],
  roundTrip: (exchangeType, selectedMapOffer, filters) => [
    ...exchangeOffersMapKeys.all,
    'round-trip',
    exchangeType,
    selectedMapOffer ? selectedMapOffer.id : null,
    selectedMapOffer && selectedMapOffer.from && selectedMapOffer.from.location
      ? [
          selectedMapOffer.from.location.id || null,
          selectedMapOffer.from.location.countryCode || null,
          selectedMapOffer.from.location.name || null
        ]
      : null,
    selectedMapOffer && selectedMapOffer.to
      ? selectedMapOffer.to.rawDate || selectedMapOffer.to.date
      : null,
    selectedMapOffer && selectedMapOffer.to && selectedMapOffer.to.mapLocation
      ? selectedMapOffer.to.mapLocation.coordinates
      : null,
    {
      exchangeTypes: filters.exchangeTypes,
      daysAhead: filters.daysAhead,
      maxDistanceKm: filters.maxDistanceKm
    }
  ],
  route: (from, to) => [
    ...exchangeOffersMapKeys.all,
    'route',
    getRouteCacheKey(from, to)
  ]
}

const getAllMapListPages = async ({ getPage, normalize }) => {
  const firstPage = await getPage(1)
  const pagination = (firstPage.response && firstPage.response.meta) || {}
  const reportedLastPage = Number(pagination.lastPage)
  const lastPage =
    Number.isSafeInteger(reportedLastPage) && reportedLastPage > 0
      ? reportedLastPage
      : 1
  const offersById = new Map()
  const appendOffers = result => {
    const offers = result.data || []
    offers.forEach(offer => offersById.set(offer.id, offer))
  }
  const pages = [firstPage]

  let nextPage = 2
  let stopped = false
  const loadNextPage = async () => {
    if (stopped || nextPage > lastPage) return

    const page = nextPage
    nextPage += 1
    try {
      pages[page - 1] = await getPage(page)
    } catch (error) {
      stopped = true
      throw error
    }
    return loadNextPage()
  }
  const workerCount = Math.min(
    MAP_LIST_PAGE_REQUEST_CONCURRENCY,
    Math.max(lastPage - 1, 0)
  )
  await Promise.all(Array.from({ length: workerCount }, loadNextPage))
  // Keep server order (including duplicate precedence) despite response timing.
  pages.forEach(appendOffers)

  return normalize(Array.from(offersById.values()).map(withRawOffer))
}

export const getRoundTripCandidateOffers = async (
  exchangeType,
  selectedMapOffer,
  roundTripFilters,
  signal
) => {
  if (isPreplannedRoundTripOffer(selectedMapOffer)) {
    return {
      offers: [],
      mapLayers: [],
      successfulExchangeTypes: [],
      failedExchangeTypes: []
    }
  }
  const selectedDestinationDateValue = selectedMapOffer.to.rawDate
  const selectedDestinationMoment = moment(selectedDestinationDateValue)
  const selectedDestinationDate = selectedDestinationMoment.isValid()
    ? selectedDestinationMoment.format('YYYY-MM-DD')
    : selectedDestinationDateValue
  if (!selectedDestinationDateValue) {
    return { offers: [], mapLayers: [] }
  }

  const candidateRequests = getRoundTripCandidateTypes(exchangeType)
    .filter(candidateExchangeType =>
      roundTripFilters.exchangeTypes.includes(candidateExchangeType)
    )
    .filter(
      candidateExchangeType =>
        candidateExchangeType !== EXCHANGE_MAP_TYPES.GENERAL ||
        process.env.GATSBY_HIDE_GENERAL_CARGO !== 'true'
    )
    .map(candidateExchangeType => ({
      exchangeType: candidateExchangeType,
      exchange: MAP_EXCHANGES[candidateExchangeType],
      normalize: MAP_NORMALIZERS[candidateExchangeType],
      filterConfig: ROUND_TRIP_FILTER_CONFIG[candidateExchangeType]
    }))
  const results = await Promise.all(
    candidateRequests.map(async candidateRequest => {
      try {
        const { filterConfig } = candidateRequest
        const filter = {
          [filterConfig.originDateFrom]: selectedDestinationDate,
          [filterConfig.originDateTo]: moment(selectedDestinationDate)
            .add(roundTripFilters.daysAhead, 'day')
            .format('YYYY-MM-DD')
        }
        // Destination matching also accepts name aliases with different IDs.
        // Apply it locally instead of narrowing the API request to one ID.
        const mapResult = await requestWithSignal(signal, config =>
          ExchangeService.getOffersMap(
            candidateRequest.exchange,
            getFormattedMapFilters(filter),
            config
          )
        )
        const mapLayers = [
          {
            exchangeType: candidateRequest.exchangeType,
            layers: mapResult.data.layers || []
          }
        ]
        const allOriginGroups = normalizeMapLayers(
          mapResult.data.layers
        ).filter(group => group.role === 'origin')
        const roundTripCenterMapLocation = getRoundTripCenterMapLocation(
          selectedMapOffer,
          allOriginGroups.map(group => group.mapLocation)
        )
        const originGroups = allOriginGroups.filter(group => {
          if (!roundTripCenterMapLocation) {
            return isSameOfferLocation(
              selectedMapOffer.to.location,
              group.mapLocation
            )
          }

          const distanceKm = getMapDistanceKm(
            roundTripCenterMapLocation,
            group.mapLocation
          )
          return (
            distanceKm !== null &&
            distanceKm <= Number(roundTripFilters.maxDistanceKm)
          )
        })
        const locationIds = Array.from(
          new Set(
            originGroups.reduce(
              (ids, group) => ids.concat(group.locationIds),
              []
            )
          )
        )
        if (locationIds.length === 0) {
          return { error: false, offers: [], mapLayers }
        }

        try {
          const offers = await getAllMapListPages({
            getPage: page =>
              requestWithSignal(signal, config =>
                ExchangeService.getOffersMapList(
                  candidateRequest.exchange,
                  {
                    layer: 'origin',
                    locationIds,
                    filter,
                    page,
                    limit: MAP_LIST_PAGE_LIMIT
                  },
                  config
                )
              ),
            normalize: candidateRequest.normalize
          })
          return {
            error: false,
            offers: offers.map(offer => ({
              exchangeType: candidateRequest.exchangeType,
              offer,
              mapLayers: mapResult.data.layers
            })),
            mapLayers
          }
        } catch (error) {
          return { error: true, offers: [], mapLayers }
        }
      } catch (error) {
        return { error: true, offers: [], mapLayers: [] }
      }
    })
  )

  if (signal && signal.aborted) {
    throw new Error('Return offer search was cancelled')
  }

  const successfulResults = results.filter(result => !result.error)
  if (results.length > 0 && successfulResults.length === 0) {
    throw new Error('Failed to load return offers')
  }

  return {
    successfulExchangeTypes: candidateRequests
      .filter((request, index) => !results[index].error)
      .map(request => request.exchangeType),
    failedExchangeTypes: candidateRequests
      .filter((request, index) => results[index].error)
      .map(request => request.exchangeType),
    offers: successfulResults.reduce(
      (combinedOffers, result) => combinedOffers.concat(result.offers),
      []
    ),
    mapLayers: successfulResults.reduce(
      (combinedLayers, result) => combinedLayers.concat(result.mapLayers),
      []
    )
  }
}

// React Query supplies previous data only from this exact search key. Keep
// failed exchanges until they recover; a successful empty result still removes
// their old offers. Preserve structural sharing for unchanged rows and layers.
const shareRoundTripResults = (previous, next) => {
  const failedTypes = new Set(next.failedExchangeTypes || [])
  if (!previous || failedTypes.size === 0) {
    return replaceEqualDeep(previous, next)
  }

  const keepFailed = items =>
    items.filter(item => failedTypes.has(item.exchangeType))
  return replaceEqualDeep(previous, {
    ...next,
    offers: next.offers.concat(keepFailed(previous.offers)),
    mapLayers: next.mapLayers.concat(keepFailed(previous.mapLayers))
  })
}

export const useExchangeMapPointsQuery = ({
  exchangeType,
  activeTab,
  filters,
  enabled
}) => {
  const exchange = MAP_EXCHANGES[exchangeType]

  return useQuery({
    queryKey: exchangeOffersMapKeys.points(exchangeType, activeTab, filters),
    queryFn: async ({ signal }) => {
      const result = await requestWithSignal(signal, config =>
        ExchangeService.getOffersMapByTab(exchange, activeTab, filters, config)
      )

      return {
        layers: result.data.layers || [],
        offersCount:
          Number(
            result.response &&
              result.response.meta &&
              result.response.meta.offersCount
          ) || 0
      }
    },
    enabled: Boolean(enabled && exchange && activeTab),
    staleTime: MAP_POINTS_STALE_TIME,
    cacheTime: MAP_CACHE_TIME,
    retry: 1,
    refetchOnWindowFocus: false
  })
}

export const useExchangeMapOffersQuery = ({
  exchangeType,
  activeTab,
  group,
  filters,
  page,
  enabled
}) => {
  const exchange = MAP_EXCHANGES[exchangeType]
  const normalize = MAP_NORMALIZERS[exchangeType]
  const groupKey = getMapGroupKey(group)

  return useQuery({
    queryKey: exchangeOffersMapKeys.list(
      exchangeType,
      activeTab,
      group,
      filters,
      page
    ),
    queryFn: async ({ signal }) => {
      const result = await requestWithSignal(signal, config =>
        ExchangeService.getOffersMapList(
          exchange,
          {
            layer: group.role,
            locationIds: group.locationIds,
            filter: filters,
            page,
            limit: MAP_LIST_PAGE_LIMIT
          },
          config
        )
      )

      return {
        groupKey,
        offers: normalize((result.data || []).map(withRawOffer)),
        pagination: (result.response && result.response.meta) || {}
      }
    },
    enabled: Boolean(enabled && exchange && normalize && group),
    keepPreviousData: true,
    staleTime: MAP_LIST_STALE_TIME,
    cacheTime: MAP_CACHE_TIME,
    retry: 1,
    refetchOnWindowFocus: false
  })
}

export const useExchangeRoundTripQuery = ({
  exchangeType,
  selectedMapOffer,
  filters,
  enabled,
  liveRefresh = false
}) =>
  useQuery({
    queryKey: exchangeOffersMapKeys.roundTrip(
      exchangeType,
      selectedMapOffer,
      filters
    ),
    queryFn: ({ signal }) =>
      getRoundTripCandidateOffers(
        exchangeType,
        selectedMapOffer,
        filters,
        signal
      ),
    enabled: Boolean(enabled && selectedMapOffer),
    structuralSharing: shareRoundTripResults,
    staleTime: ROUND_TRIP_STALE_TIME,
    cacheTime: MAP_CACHE_TIME,
    retry: false,
    retryOnMount: false,
    refetchOnMount: true,
    refetchOnWindowFocus: liveRefresh ? 'always' : true,
    refetchOnReconnect: liveRefresh ? 'always' : false
  })

export const invalidateExchangeMapQueries = (
  queryClient,
  exchangeType,
  activeTab
) => {
  scheduleMapQueryRefresh(queryClient, {
    queryKey: exchangeOffersMapKeys.pointsScope(exchangeType, activeTab)
  })
  scheduleMapQueryRefresh(queryClient, {
    queryKey: exchangeOffersMapKeys.listsScope(exchangeType, activeTab)
  })
}

export const fetchExchangeOfferRoute = (queryClient, from, to) =>
  queryClient.fetchQuery({
    queryKey: exchangeOffersMapKeys.route(from, to),
    queryFn: () => getLocallyCachedRoute(from, to),
    staleTime: ROUTE_STALE_TIME,
    cacheTime: ROUTE_CACHE_TIME,
    retry: 1
  })

export { getMapGroupKey }
